import { Button, cn, Input, Label } from '@repo/ui';
import { type ReactElement, type ReactNode, useId, useState } from 'react';
import type { ZodTypeAny } from 'zod';

/**
 * AuthForm — lightweight scaffold for the auth pages.
 *
 * Design decision: NOT using TanStack Form. The auth surface is three
 * single-field forms (Login, ForgotPassword, ResetPassword) — each does
 * ONE HTTP round-trip on submit and nothing else. A controlled-state
 * implementation:
 *   - avoids the 12-generic-argument ceremony that TanStack Form 1.33
 *     demands (TOnMount, TOnChange, TOnChangeAsync, ...);
 *   - keeps the bundle lean (no full TanStack Form runtime is pulled
 *     into the login page);
 *   - keeps the validation rules in plain Zod schemas the same place
 *     we'd find them if we DID upgrade to TanStack Form later.
 *
 * Validation strategy:
 *   - Field-level: HTML5 `required`, `type`, `minLength`, `pattern`
 *     show inline errors via `setCustomValidity` + a re-validation
 *     handler. No JS-state mirror of "valid".
 *   - Form-level: Zod parses the full state on submit. Server-side
 *     errors (401, 403) are surfaced via `useSignin` as toasts and
 *     via the optional `serverError` prop so they can also live
 *     above the form.
 *   - The schema is the SINGLE source of truth. If we ever DO want
 *     field-level "show on change" errors, swap this scaffold for
 *     TanStack Form and pass the same Zod schema through.
 */
export interface AuthFieldDef {
  name: string;
  label: string;
  type: 'text' | 'email' | 'password' | 'tel';
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;
  minLength?: number;
  pattern?: string;
  inputMode?: 'text' | 'email' | 'numeric' | 'tel' | 'url' | 'search';
}

export interface AuthFormProps<TValues extends Record<string, string>> {
  schema: ZodTypeAny;
  fields: ReadonlyArray<{
    name: keyof TValues & string;
    def: AuthFieldDef;
  }>;
  defaults: TValues;
  onValidSubmit: (values: TValues) => Promise<void> | void;
  submitLabel: string;
  submitting?: boolean;
  serverError?: string | null;
  className?: string;
  children?: ReactNode;
}

export function AuthForm<TValues extends Record<string, string>>(
  props: AuthFormProps<TValues>,
): ReactElement {
  const formId = useId();
  const [values, setValues] = useState<TValues>(props.defaults);
  const [errors, setErrors] = useState<Partial<Record<keyof TValues & string, string>>>({});
  const [formError, setFormError] = useState<string | null>(props.serverError ?? null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setFormError(null);
    setErrors({});

    const result = props.schema.safeParse(values);
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof TValues & string, string>> = {};
      for (const issue of result.error.issues) {
        const path = issue.path[0];
        if (typeof path === 'string') {
          fieldErrors[path as keyof TValues & string] = issue.message;
        } else {
          setFormError(issue.message);
        }
      }
      if (Object.keys(fieldErrors).length > 0) setErrors(fieldErrors);
      return;
    }

    try {
      await props.onValidSubmit(result.data as TValues);
    } catch (err) {
      // Surface synchronous submission errors (network down, etc.)
      // without losing user-typed values.
      const message = err instanceof Error ? err.message : 'No se pudo enviar el formulario';
      setFormError(message);
    }
  };

  return (
    <form
      id={formId}
      noValidate
      onSubmit={handleSubmit}
      className={cn('grid gap-4', props.className)}
    >
      {props.fields.map(({ name, def }) => (
        <AuthField
          key={name}
          {...def}
          id={name}
          value={values[name] ?? ''}
          onChange={(v) => {
            setValues((prev) => ({ ...prev, [name]: v }));
            if (errors[name]) {
              setErrors((prev) => {
                const next = { ...prev };
                delete next[name];
                return next;
              });
            }
          }}
          {...(errors[name] ? { error: errors[name] } : {})}
        />
      ))}

      {formError ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/20 bg-destructive/5 px-3 py-2 text-sm font-medium text-destructive"
        >
          {formError}
        </p>
      ) : null}

      {props.children}

      <Button type="submit" size="lg" disabled={props.submitting} className="mt-2 w-full">
        {props.submitting ? (
          <span className="inline-flex items-center gap-2">
            <Spinner />
            {props.submitLabel}…
          </span>
        ) : (
          props.submitLabel
        )}
      </Button>
    </form>
  );
}

interface AuthFieldProps extends AuthFieldDef {
  id: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
}

function AuthField(props: AuthFieldProps): ReactElement {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={props.id}>{props.label}</Label>
      <Input
        id={props.id}
        name={props.id}
        type={props.type}
        value={props.value}
        onChange={(e) => props.onChange(e.currentTarget.value)}
        required={props.required ?? false}
        autoComplete={props.autoComplete}
        minLength={props.minLength}
        pattern={props.pattern}
        placeholder={props.placeholder}
        inputMode={props.inputMode}
        aria-invalid={props.error ? true : undefined}
        aria-describedby={props.error ? `${props.id}-err` : undefined}
      />
      {props.error ? (
        <p id={`${props.id}-err`} className="text-xs font-medium text-destructive">
          {props.error}
        </p>
      ) : null}
    </div>
  );
}

function Spinner(): ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      className="animate-spin"
      aria-hidden="true"
      fill="none"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}
